import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';
import {
  ACTIVATE_ANNOTATION_EVENT,
  ANNOTATABLE_SELECTION_CLEAR_EVENT,
  ANNOTATABLE_SELECTION_EVENT,
  CANCEL_CREATE_ANNOTATION_EVENT,
  CREATE_ANNOTATION_EVENT,
  CREATE_TMP_ANNOTATION_EVENT,
  REMOVE_ANNOTATION_EVENT
} from '../editor-ui';
import { updateAnnotationListCacheBust } from '../store/textEditorReducer';

function getRelativePosition(coords, positionContainerRef) {
  return function stateSetter() {
    const box = positionContainerRef.current.getBoundingClientRect();
    return coords.top - box.top - 14;
  };
}

export default function useTextEditorAnnotations(
  consumerApi,
  contentId,
  allContentIds,
  positionContainerRef
) {
  const [showCommentButton, setShowCommentButton] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [activeAnnotation, setActiveAnnotation] = useState(null);
  const [highlightedText, setHighlightedText] = useState(null);
  const [yPos, setYPos] = useState('0px');
  const { resolvedAnnotation } = useSelector(state => state.textEditor);
  const dispatch = useDispatch();

  // Get the filter state from the Redux store or from a parent component
  const { filterState } = useSelector(state => state.textEditor) || { filterState: 'All comments' };

  useEffect(() => {
    consumerApi.on(ANNOTATABLE_SELECTION_EVENT, ({ coords, highlightedText: selectedText }) => {
      setYPos(getRelativePosition(coords, positionContainerRef));
      setShowCommentButton(true);
      setHighlightedText(selectedText);
    });
    consumerApi.on(CREATE_ANNOTATION_EVENT, ({ markId }) => {
      setShowCommentForm(false);
      setActiveAnnotation(markId);
    });
    consumerApi.on(ANNOTATABLE_SELECTION_CLEAR_EVENT, () => setShowCommentButton(false));
    consumerApi.on(ACTIVATE_ANNOTATION_EVENT, ({ annotationUUID, coords }) => {
      setYPos(getRelativePosition(coords, positionContainerRef));
      setActiveAnnotation(annotationUUID);
    });
  }, [consumerApi, positionContainerRef]);

  /*
   * This effect is used to register the handler for Active Annotation events.
   * On the Edit Page, this is done via the consumerApi object, but here
   * we don't have easy access to a working one, and so we use plain old JS
   * event listeners instead.
   */
  useEffect(() => {
    /*
     * The window check ensures that only one handler is defined across all
     * Prosemirror Editors on the page (ie, let's use the same handler for Headline,
     * Body, Custom Fields, etc)
     */
    if (window.activeAnnotationHandlerLoaded === undefined) {
      const handleActivateAnnotation = e => {
        const { annotationUUID, coords } = e.detail;
        setYPos(getRelativePosition(coords, positionContainerRef));
        setActiveAnnotation(annotationUUID);
      };

      document.addEventListener(ACTIVATE_ANNOTATION_EVENT, handleActivateAnnotation, false);

      window.activeAnnotationHandlerLoaded = true;

      return () => {
        document.removeEventListener(ACTIVATE_ANNOTATION_EVENT, handleActivateAnnotation, false);
        window.activeAnnotationHandlerLoaded = undefined;
      };
    }

    return undefined;
  }, [positionContainerRef]);

  useEffect(() => {
    if (resolvedAnnotation) {
      consumerApi.emit(REMOVE_ANNOTATION_EVENT, resolvedAnnotation);
      setActiveAnnotation(null);
    }
  }, [consumerApi, resolvedAnnotation]);

  const handleNewAnnotation = () => {
    consumerApi.emit(CREATE_TMP_ANNOTATION_EVENT, setShowCommentForm(true));
    setShowCommentButton(false);
  };

  const handleSubmitComment = async ({ body }) => {
    const data = {
      target_type: 'Content',
      target_id: contentId,
      type: 'Annotation::Text',
      mark_id: uuid(),
      highlightedText,
      allContentIds,
      comment_attributes: {
        body
      },
      setShowCommentForm
    };

    dispatch(updateAnnotationListCacheBust(true));
    if (window.refreshCommentsTab) window.refreshCommentsTab();

    const actionsOther = $('body').find('.actions-other');
    if (!actionsOther.hasClass('allow-comment')) {
      actionsOther.addClass('allow-comment');
    }

    consumerApi.emit(CREATE_ANNOTATION_EVENT, data);
  };

  const handleCancelComment = () => {
    consumerApi.emit(CANCEL_CREATE_ANNOTATION_EVENT, setShowCommentForm(false));
  };

  return {
    showCommentButton,
    setShowCommentButton,
    showCommentForm,
    activeAnnotation,
    setActiveAnnotation,
    yPos,
    handleNewAnnotation,
    handleSubmitComment,
    handleCancelComment,
    filterState
  };
}



// WEBPACK FOOTER //
// ./app/javascript/client/hooks/useTextEditorAnnotations.js